import useCalendarSelection from "../../../../entityProfileModal/sections/calendar/useCalendarSelection";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Checkbox,
  HStack,
  Stack,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import type { Messages } from "../../../../../core/api";
import { auth } from "../../../../../core/auth";
import useApi from "../../../../../shared/hooks/useApi";
import useLocalStorage from "../../../../../shared/hooks/useLocalStorage";
import {
  CaregiverAbsenceId,
  CaregiverId,
  VisitInstanceId,
} from "../../../../../shared/schema/schema";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import EntityProfileCalendar from "../../../../entityProfileModal/sections/calendar/EntityProfileCalendar";
import {
  formatEntityCalendarDate,
  formatEntityCalendarTime,
  getCaregiverCalendarVisitBroadcastKind,
  getEntityCalendarVisibleMonthDates,
  toEntityCalendarLocalDate,
  toEntityCalendarLocalDateTime,
  type EntityProfileCalendarMarker,
  type EntityProfileCalendarPtoRow,
  type EntityProfileCalendarVisit,
} from "../../../../entityProfileModal/sections/calendar/entityProfileCalendar.utils";
import type { CaregiverProfileBridgeActions } from "../../../caregiverProfileModal.actions";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import {
  getCaregiverCalendarQueryKeyPrefix,
  useCaregiverProfileCalendarRange,
} from "./CaregiverProfileCalendarSection.hooks";
import type {
  CaregiverCalendarAbsenceLike,
  CaregiverCalendarNoteLike,
  CaregiverCalendarPtoLike,
  CaregiverCalendarVisitLike,
} from "./caregiverCalendar.types";

type CalendarView = "calendar" | "list";

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiver?: CaregiverProfileCaregiver;
  caregiverId: CaregiverId;
  /**
   * The card is kept mounted but hidden (the "save calendar selection" setting
   * while another section is open). It then loads nothing until shown again.
   */
  isHidden?: boolean;
  view: CalendarView;
};

const showDeletedVisitsStorageKey = "caregiverCalendarShowDeletedVisits";
const showSummaryColumnStorageKey = "caregiverCalendarShowSummaryColumn";
const entityProfileCalendarReadyEvent = "medflyt:entity-profile-calendar-ready";

function isDateInRange(date: LocalDate, from: LocalDate, to: LocalDate) {
  return date.isBefore(from) === false && date.isAfter(to) === false;
}

function hasGpsIssue(visit: EntityProfileCalendarVisit) {
  return visit.authorizationStatuses?.some((status) =>
    status.message.toLowerCase().includes("gps")
  );
}

function getVisitStatus(visit: CaregiverCalendarVisitLike): EntityProfileCalendarVisit["status"] {
  if (visit.visit.removedAt) {
    return "deleted";
  }

  if (visit.visit.missedVisit) {
    return "missed";
  }

  if (visit.taskDetails?.taskName) {
    return "task";
  }

  return "visit";
}

function getVisitAuthorizationStatuses(visit: CaregiverCalendarVisitLike) {
  const statuses: EntityProfileCalendarVisit["authorizationStatuses"] = [];

  if (
    visit.missingAuth ||
    visit.issue_invalid_authorization_assignment ||
    visit.issue_no_matching_authorization
  ) {
    statuses.push({
      message: "No Authorization",
      tooltip:
        visit.issue_invalid_authorization_assignment ??
        visit.issue_no_matching_authorization ??
        undefined,
    });
  }

  if (visit.issue_missing_authorization_hours) {
    statuses.push({
      message: "Auth Insufficient Hours",
      tooltip: visit.issue_missing_authorization_hours,
    });
  }

  if (visit.issue_authorization_over_allocation) {
    statuses.push({
      message: "Auth Over Allocation",
      tooltip: visit.issue_authorization_over_allocation,
    });
  }

  if (visit.issue_schedule_auth_allocation_conflict) {
    statuses.push({
      message: "Auth Conflict",
      tooltip: visit.issue_schedule_auth_allocation_conflict,
    });
  }

  if (visit.issue_missing_clock_times) {
    statuses.push({
      message: "Missing Clock Times",
      tooltip: visit.issue_missing_clock_times,
    });
  }

  if (visit.issue_excessive_clock_distance) {
    statuses.push({
      backgroundColor: "#f39c12",
      message: "GPS Location Mismatch",
      tooltip: visit.issue_excessive_clock_distance,
    });
  }

  return statuses.length > 0 ? statuses : null;
}

function getVisitPatientAddress(visit: CaregiverCalendarVisitLike) {
  const address = visit.visit.address;

  if (typeof address === "string") {
    return address;
  }

  return address?.address ?? address?.text ?? null;
}

function getVisitTitle(visit: CaregiverCalendarVisitLike) {
  return visit.patientDisplayName ?? visit.visit.patientName ?? "Patient";
}

function normalizeVisit(visit: CaregiverCalendarVisitLike): EntityProfileCalendarVisit {
  const normalized: EntityProfileCalendarVisit = {
    addressTimezone: visit.visit.address?.timezone,
    authorizationStatuses: getVisitAuthorizationStatuses(visit),
    billedSeconds: visit.billedSeconds,
    caregiverId: visit.visit.caregiverId,
    certification: visit.certification,
    clockinTime: visit.visit.clockinTime,
    clockoutTime: visit.visit.clockoutTime,
    date: visit.visitDate,
    endTime: visit.visit.endTime,
    id: visit.visit.id,
    isOnWeeklyTemplate: visit.isOnWeeklyTemplate,
    isPaid: visit.isPaid,
    paidSeconds: visit.paidSeconds ?? visit.draftPaidSeconds,
    payrollDraftSeconds: visit.draftPaidSeconds,
    patientAddress: getVisitPatientAddress(visit),
    patientId: visit.visit.patientId,
    parentSplitVisitInstanceId: visit.parentSplitVisitInstanceId,
    childSplitVisitInstanceId: visit.childSplitVisitInstanceId,
    pendingConfirmationUntil: visit.pendingConfirmationUntil,
    startTime: visit.visit.startTime,
    status: getVisitStatus(visit),
    subtitle: visit.taskDetails?.taskName ?? undefined,
    title: getVisitTitle(visit),
    visitBatchType: visit.visitBatchType,
    visitBroadcast: visit.visitBroadcast,
  };

  return { ...normalized, broadcastKind: getCaregiverCalendarVisitBroadcastKind(normalized) };
}

function normalizeNote(note: CaregiverCalendarNoteLike) {
  return {
    date: note.calendarDate,
    id: note.id,
  };
}

function normalizePto(pto: CaregiverCalendarPtoLike): EntityProfileCalendarMarker | null {
  if (pto.visitInstance?.startTime === undefined) {
    return null;
  }

  return {
    colorScheme: pto.status === "APPROVED" ? "green" : "orange",
    date: toEntityCalendarLocalDateTime(pto.visitInstance.startTime).toLocalDate(),
    detailLabel:
      pto.duration === undefined
        ? undefined
        : `${pto.duration} min PTO${
            pto.originalVisitMinutes === undefined ? "" : ` of ${pto.originalVisitMinutes} min`
          }`,
    id: `pto-${pto.id}`,
    label: pto.status ? `PTO ${pto.status.toLowerCase()}` : "PTO",
    summaryKind: "pto",
    summarySeconds: pto.paidSeconds ?? (pto.duration === undefined ? undefined : pto.duration * 60),
  };
}

function getPtoPatientAddress(pto: CaregiverCalendarPtoLike) {
  const address = pto.visitInstance?.address;

  if (typeof address === "string") {
    return address;
  }

  return address?.address ?? address?.text ?? null;
}

function getPtoStatusLabel(status: CaregiverCalendarPtoLike["status"]) {
  if (status === "APPROVED") {
    return "Approved";
  }

  if (status === "DECLINED") {
    return "Declined";
  }

  if (status === "PENDING") {
    return "Pending";
  }

  return null;
}

function getPtoPatientName(pto: CaregiverCalendarPtoLike) {
  return pto.patientDisplayName ?? pto.visitInstance?.patientName ?? "Patient";
}

function normalizePtoRow(pto: CaregiverCalendarPtoLike): EntityProfileCalendarPtoRow | null {
  const startTime = pto.visitInstance?.startTime;

  if (startTime === undefined) {
    return null;
  }

  return {
    date: toEntityCalendarLocalDateTime(startTime).toLocalDate(),
    endTime:
      pto.visitInstance?.endTime ??
      (pto.duration === undefined
        ? null
        : toEntityCalendarLocalDateTime(startTime).plusMinutes(pto.duration)),
    id: pto.id,
    label: pto.labelText ?? pto.ptoLabel?.text ?? null,
    patientAddress: getPtoPatientAddress(pto),
    patientId: pto.visitInstance?.patientId ?? null,
    patientName: getPtoPatientName(pto),
    startTime,
    statusLabel: getPtoStatusLabel(pto.status),
  };
}

function normalizeAbsence(
  absence: CaregiverCalendarAbsenceLike,
  onDeleteAbsence: (absenceIds: CaregiverAbsenceId[]) => void
): EntityProfileCalendarMarker {
  return {
    actions: [{ label: "Delete", onClick: () => onDeleteAbsence([absence.id]) }],
    colorScheme: "red",
    date: absence.date,
    id: `absence-${absence.id}`,
    label: "Absence",
  };
}

function CaregiverCalendarActionButtons(props: {
  selectedAbsenceIds: CaregiverAbsenceId[];
  selectedVisits: EntityProfileCalendarVisit[];
  onDeleteAbsences: () => void;
  onSetPto: () => void;
  onApproveClockLocation: () => void;
}) {
  const hasSelectedVisits = props.selectedVisits.length > 0;
  const hasSelectedAbsences = props.selectedAbsenceIds.length > 0;
  const hasOnlyPtoEligibleVisits =
    hasSelectedVisits &&
    hasSelectedAbsences === false &&
    props.selectedVisits.every((visit) => visit.status === "visit" || visit.status === "missed");
  const selectedVisitsHaveGpsIssues =
    hasSelectedVisits &&
    hasSelectedAbsences === false &&
    props.selectedVisits.every((visit) => hasGpsIssue(visit) === true);
  const canApproveClockLocation = auth.isAllowedTo(
    "edit_patient_visit" as Messages["AgencyMemberPermission"]
  );
  const canSetPto = auth.isAllowedTo(
    "view_caregivers_section_pto_approvals" as Messages["AgencyMemberPermission"]
  );
  return (
    <HStack flexShrink={0} flexWrap="nowrap" justifyContent="flex-end" spacing={3}>
      {canSetPto ? (
        <EntityModalButton
          disabled={!hasOnlyPtoEligibleVisits}
          kind="blue"
          size="sm"
          onClick={props.onSetPto}
        >
          Set PTO
        </EntityModalButton>
      ) : null}
      {canApproveClockLocation ? (
        <EntityModalButton
          disabled={!selectedVisitsHaveGpsIssues}
          kind="blue"
          size="sm"
          onClick={props.onApproveClockLocation}
        >
          Approve Clock Location
        </EntityModalButton>
      ) : null}
      {auth.isAllowedTo("create/delete_caregiver_absence" as Messages["AgencyMemberPermission"]) ? (
        <EntityModalButton
          disabled={props.selectedAbsenceIds.length === 0 || hasSelectedVisits}
          kind="red"
          size="sm"
          onClick={props.onDeleteAbsences}
        >
          Delete Absence
        </EntityModalButton>
      ) : null}
    </HStack>
  );
}

function CaregiverCalendarListActionButtons(props: {
  selectedVisits: EntityProfileCalendarVisit[];
  onUnassignVisits: () => void;
}) {
  const hasSelectedVisits = props.selectedVisits.length > 0;

  return (
    <HStack flexShrink={0} flexWrap="nowrap" justifyContent="flex-end" spacing={2}>
      <EntityModalButton
        disabled={!hasSelectedVisits}
        kind="red"
        size="sm"
        onClick={props.onUnassignVisits}
      >
        Unassign
      </EntityModalButton>
    </HStack>
  );
}

function CaregiverUnassignVisitsDialog(props: {
  isLoading: boolean;
  isOpen: boolean;
  visits: EntityProfileCalendarVisit[];
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog
      isCentered
      isOpen={props.isOpen}
      leastDestructiveRef={cancelRef}
      onClose={props.onCancel}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Unassign visits</AlertDialogHeader>
          <AlertDialogBody>
            <VStack alignItems="stretch" spacing={3}>
              <Text>Unassign caregiver from selected visits?</Text>
              <VStack alignItems="stretch" spacing={1}>
                {props.visits.map((visit, index) => (
                  <Text key={visit.id} fontSize="14px">
                    {index + 1}. {formatEntityCalendarDate(visit.date)}{" "}
                    {formatEntityCalendarTime(visit.startTime)} -{" "}
                    {formatEntityCalendarTime(visit.endTime)}
                  </Text>
                ))}
              </VStack>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <EntityModalButton
              ref={cancelRef}
              disabled={props.isLoading}
              kind="white"
              onClick={props.onCancel}
            >
              Cancel
            </EntityModalButton>
            <EntityModalButton
              isLoading={props.isLoading}
              kind="red"
              style={{ marginLeft: 12 }}
              onClick={props.onConfirm}
            >
              Unassign
            </EntityModalButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

function CaregiverAbsenceDeleteDialog(props: {
  absences: CaregiverCalendarAbsenceLike[];
  isLoading: boolean;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const isMultiple = props.absences.length > 1;
  const subject = isMultiple ? "Delete absences" : "Delete absence";

  return (
    <AlertDialog
      isCentered
      isOpen={props.isOpen}
      leastDestructiveRef={cancelRef}
      onClose={props.onCancel}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>{subject}</AlertDialogHeader>
          <AlertDialogBody>
            {isMultiple ? (
              <VStack alignItems="stretch" spacing={3}>
                <Text>
                  Are you sure you want to delete {props.absences.length} absences for the following
                  dates?
                </Text>
                <VStack alignItems="stretch" spacing={1}>
                  {props.absences.map((absence, index) => (
                    <Text key={absence.id} fontSize="14px">
                      {index + 1}. {formatEntityCalendarDate(absence.date)}
                    </Text>
                  ))}
                </VStack>
              </VStack>
            ) : (
              <Text>
                Are you sure you want to delete absence for{" "}
                {props.absences.length === 1
                  ? formatEntityCalendarDate(props.absences[0].date)
                  : ""}
                ?
              </Text>
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <EntityModalButton
              ref={cancelRef}
              disabled={props.isLoading}
              kind="white"
              onClick={props.onCancel}
            >
              Cancel
            </EntityModalButton>
            <EntityModalButton
              isLoading={props.isLoading}
              kind="red"
              style={{ marginLeft: 12 }}
              onClick={props.onConfirm}
            >
              Delete
            </EntityModalButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

function CaregiverCalendarFilters(props: {
  showDeletedVisits: boolean;
  showSummaryColumn: boolean;
  onChangeShowDeletedVisits: (value: boolean) => void;
  onChangeShowSummaryColumn: (value: boolean) => void;
}) {
  const checkboxStyles = {
    colorScheme: "blue" as const,
    size: "md" as const,
  };
  const labelStyles = {
    color: "#2D2D2D",
    fontSize: "14px",
    fontWeight: 400,
  };

  return (
    <Stack mb={3} spacing={2}>
      <Checkbox
        {...checkboxStyles}
        isChecked={props.showDeletedVisits}
        onChange={(event) => props.onChangeShowDeletedVisits(event.target.checked)}
      >
        <Text {...labelStyles}>Show Deleted Visits</Text>
      </Checkbox>
      <Checkbox
        {...checkboxStyles}
        isChecked={props.showSummaryColumn}
        onChange={(event) => props.onChangeShowSummaryColumn(event.target.checked)}
      >
        <Text {...labelStyles}>Show Summary Column</Text>
      </Checkbox>
    </Stack>
  );
}

export default function CaregiverProfileCalendarSection(props: Props) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [monthDate, setMonthDate] = React.useState(() => LocalDate.now());
  const [listDateRange, setListDateRange] = React.useState<[LocalDate, LocalDate | null]>(() => {
    const today = LocalDate.now();

    return [today.withDayOfMonth(1), today.plusMonths(1).withDayOfMonth(1).minusDays(1)];
  });
  const handleListDateRangeChange = React.useCallback((range: [LocalDate, LocalDate | null]) => {
    setListDateRange(range);
    setMonthDate(range[0]);
  }, []);
  const [showDeletedVisits, setShowDeletedVisits] = useLocalStorage(
    showDeletedVisitsStorageKey,
    true
  );
  const [showSummaryColumn, setShowSummaryColumn] = useLocalStorage(
    showSummaryColumnStorageKey,
    true
  );
  const [visitsToUnassign, setVisitsToUnassign] = React.useState<
    EntityProfileCalendarVisit[] | null
  >(null);
  const hasSignaledCalendarReadyRef = React.useRef(false);

  const visibleDates = React.useMemo(
    () => getEntityCalendarVisibleMonthDates(monthDate),
    [monthDate]
  );
  const listRangeEnd = listDateRange[1] ?? listDateRange[0];
  const visitsQuery = useCaregiverProfileCalendarRange(
    props.caregiverId,
    props.view === "list" ? listDateRange[0] : visibleDates.queryFrom,
    props.view === "list" ? listRangeEnd : visibleDates.queryTo,
    props.isHidden !== true
  );

  React.useEffect(() => {
    if (!visitsQuery.isFetched || hasSignaledCalendarReadyRef.current) {
      return;
    }

    const medflytWindow = window as Window & { __medflytEntityProfileCalendarReady?: boolean };

    hasSignaledCalendarReadyRef.current = true;
    medflytWindow.__medflytEntityProfileCalendarReady = true;
    medflytWindow.dispatchEvent(new Event(entityProfileCalendarReadyEvent));
  }, [visitsQuery.isFetched]);

  const response = visitsQuery.data;
  // By query key rather than through `visitsQuery`, which is a new object on
  // every render. One change usually calls this several times in the same tick
  // (the `medflyt:refresh_visits` listener of the calendar and of the list view,
  // and the action's `onDone`); `cancelRefetch: false` joins the request already
  // in flight instead of cancelling it and sending another.
  const refetchCalendar = React.useCallback(() => {
    void queryClient.refetchQueries(
      { type: "active", queryKey: getCaregiverCalendarQueryKeyPrefix(props.caregiverId) },
      { cancelRefetch: false }
    );
  }, [props.caregiverId, queryClient]);
  const allVisits = React.useMemo(
    () => (response?.visitInstances ?? []).map(normalizeVisit),
    [response?.visitInstances]
  );
  const visits = React.useMemo(
    () => (showDeletedVisits ? allVisits : allVisits.filter((visit) => visit.status !== "deleted")),
    [allVisits, showDeletedVisits]
  );
  const visibleVisits = React.useMemo(() => {
    if (props.view === "list") {
      return visits.filter((visit) =>
        isDateInRange(toEntityCalendarLocalDate(visit.date), listDateRange[0], listRangeEnd)
      );
    }

    return visits;
  }, [listDateRange, listRangeEnd, props.view, visits]);
  const notes = React.useMemo(() => (response?.notes ?? []).map(normalizeNote), [response?.notes]);
  const ptoMarkers = React.useMemo(
    () =>
      (response?.ptoInstances?.caregiverPtos ?? [])
        .map((pto) => normalizePto(pto))
        .filter((marker): marker is EntityProfileCalendarMarker => marker !== null),
    [response?.ptoInstances?.caregiverPtos]
  );
  const ptoRows = React.useMemo(
    () =>
      (response?.ptoInstances?.caregiverPtos ?? [])
        .map((pto) => normalizePtoRow(pto))
        .filter((row): row is EntityProfileCalendarPtoRow => row !== null),
    [response?.ptoInstances?.caregiverPtos]
  );
  const showCaregiverOvertimeAlerts = auth.getAuthOrFail().data.agency.id === 6205;
  const selectionRange = {
    from: props.view === "list" ? listDateRange[0] : visibleDates.queryFrom,
    to: props.view === "list" ? listRangeEnd : visibleDates.queryTo,
    isFetched: visitsQuery.isSuccess && !visitsQuery.isPlaceholderData,
  };
  const visitSelection = useCalendarSelection(visibleVisits, selectionRange);
  const {
    ids: selectedVisitIds,
    setIds: setSelectedVisitIds,
    selected: selectedVisits,
  } = visitSelection;
  const selectedListVisits = selectedVisits.filter(
    (visit) => visit.status !== "deleted" && visit.status !== "task"
  );
  const certificationOptions = React.useMemo(
    () => [
      ...new Set(
        allVisits
          .map((visit) => visit.certification)
          .filter((certification): certification is string => certification != null)
      ),
    ],
    [allVisits]
  );
  const deleteAbsencesMutation = useMutation({
    mutationFn: (absenceIds: CaregiverAbsenceId[]) =>
      api.post("./caregivers/:caregiverId/caregiver_absences/delete", {
        body: { caregiverAbsenceIds: absenceIds },
        path: { caregiverId: props.caregiverId },
      }),
    onSuccess: () => {
      setSelectedAbsenceIds([]);
      refetchCalendar();
      toast({
        description: "Selected absences successfully deleted",
        position: "top-right",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Failed to delete selected absences",
        position: "top-right",
        status: "error",
      });
    },
  });
  const unassignVisitsMutation = useMutation({
    mutationFn: (visitIds: VisitInstanceId[]) =>
      api.post("./bulk_edit_visit_instance", {
        body: {
          changes: { caregiverId: null },
          ids: visitIds,
        },
      }),
    onSuccess: () => {
      setVisitsToUnassign(null);
      setSelectedVisitIds([]);
      refetchCalendar();
      toast({
        description: "Caregiver unassigned successfully",
        position: "top-right",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Can't unassign from selected visits",
        position: "top-right",
        status: "error",
      });
    },
  });
  const [absencesToDelete, setAbsencesToDelete] = React.useState<
    CaregiverCalendarAbsenceLike[] | null
  >(null);
  const handleDeleteAbsences = React.useCallback(
    (absenceIds: CaregiverAbsenceId[]) => {
      if (absenceIds.length === 0) {
        return;
      }

      const absencesById = new Map(
        (response?.absences ?? []).map((absence) => [absence.id, absence])
      );
      const targets = absenceIds
        .map((absenceId) => absencesById.get(absenceId))
        .filter((absence): absence is CaregiverCalendarAbsenceLike => absence !== undefined);

      if (targets.length === 0) {
        return;
      }

      setAbsencesToDelete(targets);
    },
    [response?.absences]
  );
  const handleConfirmDeleteAbsences = () => {
    if (absencesToDelete === null) {
      return;
    }

    deleteAbsencesMutation.mutate(
      absencesToDelete.map((absence) => absence.id),
      { onSuccess: () => setAbsencesToDelete(null) }
    );
  };
  const visibleAbsences = React.useMemo(() => {
    const absences = response?.absences ?? [];

    if (props.view === "list") {
      return absences.filter((absence) => {
        const absenceDate = toEntityCalendarLocalDate(absence.date);

        return (
          absenceDate.isBefore(listDateRange[0]) === false &&
          absenceDate.isAfter(listDateRange[1] ?? listDateRange[0]) === false
        );
      });
    }

    const visibleDateKeys = new Set(visibleDates.dates.map((date) => date.toString()));

    return absences.filter((absence) =>
      visibleDateKeys.has(toEntityCalendarLocalDate(absence.date).toString())
    );
  }, [listDateRange, props.view, response?.absences, visibleDates.dates]);
  const absenceSelection = useCalendarSelection(visibleAbsences, selectionRange);
  const {
    ids: selectedAbsenceIds,
    setIds: setSelectedAbsenceIds,
    selected: selectedAbsences,
  } = absenceSelection;
  React.useEffect(() => {
    const handleRefreshVisits = () => {
      setSelectedVisitIds([]);
      setSelectedAbsenceIds([]);
      refetchCalendar();
    };

    window.addEventListener("medflyt:refresh_visits", handleRefreshVisits);

    return () => window.removeEventListener("medflyt:refresh_visits", handleRefreshVisits);
  }, [refetchCalendar, setSelectedVisitIds, setSelectedAbsenceIds]);

  const absenceMarkers = React.useMemo(
    () =>
      (response?.absences ?? []).map((absence) => normalizeAbsence(absence, handleDeleteAbsences)),
    [handleDeleteAbsences, response?.absences]
  );
  const markers = React.useMemo(
    () => [...ptoMarkers, ...absenceMarkers],
    [absenceMarkers, ptoMarkers]
  );
  const handleToggleVisitSelection = visitSelection.toggle;
  const handleToggleMarkerSelection = (marker: EntityProfileCalendarMarker) => {
    const markerId = String(marker.id);

    if (markerId.startsWith("absence-") === false) {
      return;
    }

    const absence = visibleAbsences.find((item) => `absence-${item.id}` === markerId);

    if (absence === undefined) {
      return;
    }

    absenceSelection.toggle(absence);
  };
  const handleListVisibleSelectionChange = React.useCallback(
    (selection: { markerIds: string[]; visitIds: VisitInstanceId[] }) => {
      const visibleVisitIds = new Set(selection.visitIds);
      const visibleMarkerIds = new Set(selection.markerIds);
      const visibleAbsenceIds = new Set(
        visibleAbsences
          .filter((absence) => visibleMarkerIds.has(`absence-${absence.id}`))
          .map((absence) => absence.id)
      );

      setSelectedVisitIds((current) =>
        filterPreservingIdentity(current, (selectedVisitId) => visibleVisitIds.has(selectedVisitId))
      );
      setSelectedAbsenceIds((current) =>
        filterPreservingIdentity(current, (selectedAbsenceId) =>
          visibleAbsenceIds.has(selectedAbsenceId)
        )
      );
    },
    [visibleAbsences, setSelectedVisitIds, setSelectedAbsenceIds]
  );
  const openSetPto = React.useCallback(
    (visitsForPto: EntityProfileCalendarVisit[]) => {
      if (props.caregiver === undefined) {
        toast({
          description: "Caregiver profile details are still loading.",
          position: "top-right",
          status: "info",
        });
        return;
      }

      const ptoEligibleVisits = visitsForPto.filter(
        (visit) => visit.status === "visit" || visit.status === "missed"
      );

      if (ptoEligibleVisits.length === 0) {
        toast({
          description: "There are no assigned visits in the selected range.",
          position: "top-right",
          status: "error",
        });
        return;
      }

      props.actions.setCalendarPto({
        caregiver: props.caregiver,
        onDone: refetchCalendar,
        selectedVisits: ptoEligibleVisits,
      });
    },
    [props.actions, props.caregiver, refetchCalendar, toast]
  );
  const openApproveClockLocation = React.useCallback(
    (visitsForApproval: EntityProfileCalendarVisit[]) => {
      if (props.caregiver === undefined) {
        toast({
          description: "Caregiver profile details are still loading.",
          position: "top-right",
          status: "info",
        });
        return;
      }

      props.actions.approveCalendarClockLocation({
        caregiver: props.caregiver,
        onDone: refetchCalendar,
        selectedVisits: visitsForApproval,
      });
    },
    [props.actions, props.caregiver, refetchCalendar, toast]
  );
  const dayMenuItems = React.useCallback(
    (context: { date: LocalDate }) => {
      const dateAbsences = (response?.absences ?? []).filter((absence) =>
        toEntityCalendarLocalDate(absence.date).equals(context.date)
      );
      const dateVisits = visits.filter((visit) =>
        toEntityCalendarLocalDate(visit.date).equals(context.date)
      );
      const hasAbsence = dateAbsences.length > 0;
      const items = [
        {
          label: "New Note",
          onClick: () =>
            props.caregiver === undefined
              ? toast({
                  description: "Caregiver profile details are still loading.",
                  position: "top-right",
                  status: "info",
                })
              : props.actions.openCalendarNote({
                  calendarDate: context.date.toString(),
                  caregiver: props.caregiver,
                  onDone: refetchCalendar,
                }),
        },
        {
          isDisabled: dateVisits.every(
            (visit) => visit.status !== "visit" && visit.status !== "missed"
          ),
          label: "Set In PTO",
          onClick: () => openSetPto(dateVisits),
        },
      ];

      if (
        auth.isAllowedTo("create/delete_caregiver_absence" as Messages["AgencyMemberPermission"])
      ) {
        items.push(
          hasAbsence
            ? {
                label: "Delete Absence",
                onClick: () => handleDeleteAbsences(dateAbsences.map((absence) => absence.id)),
              }
            : {
                label: "Set In Absence",
                onClick: () =>
                  props.caregiver === undefined
                    ? toast({
                        description: "Caregiver profile details are still loading.",
                        position: "top-right",
                        status: "info",
                      })
                    : props.actions.openAbsence({
                        calendarDate: context.date.toString(),
                        caregiver: props.caregiver,
                        onDone: refetchCalendar,
                      }),
              }
        );
      }

      return items;
    },
    [
      handleDeleteAbsences,
      openSetPto,
      props.actions,
      props.caregiver,
      refetchCalendar,
      response?.absences,
      toast,
      visits,
    ]
  );
  const handleUnassignVisits = () => {
    if (selectedListVisits.length === 0) {
      return;
    }

    setVisitsToUnassign(selectedListVisits);
  };
  const handleConfirmUnassignVisits = () => {
    if (visitsToUnassign === null) {
      return;
    }

    unassignVisitsMutation.mutate(visitsToUnassign.map((visit) => visit.id));
  };
  const handleSetPto = () => {
    openSetPto(selectedVisits);
  };
  const handleApproveClockLocation = () => {
    openApproveClockLocation(selectedVisits);
  };
  const handleDeleteSelectedAbsences = () => {
    if (selectedAbsences.length === 0) {
      return;
    }

    setAbsencesToDelete(selectedAbsences);
  };

  return (
    <Box>
      {props.view === "calendar" ? (
        <CaregiverCalendarFilters
          showDeletedVisits={showDeletedVisits}
          showSummaryColumn={showSummaryColumn}
          onChangeShowDeletedVisits={setShowDeletedVisits}
          onChangeShowSummaryColumn={setShowSummaryColumn}
        />
      ) : null}
      <EntityProfileCalendar
        actionButtons={
          <CaregiverCalendarActionButtons
            selectedAbsenceIds={selectedAbsenceIds}
            selectedVisits={selectedVisits}
            onApproveClockLocation={handleApproveClockLocation}
            onDeleteAbsences={handleDeleteSelectedAbsences}
            onSetPto={handleSetPto}
          />
        }
        dayMenuItems={dayMenuItems}
        errorLabel="Unable to load caregiver calendar."
        isError={visitsQuery.isError}
        isLoading={visitsQuery.isLoading}
        listActionButtons={
          <CaregiverCalendarListActionButtons
            selectedVisits={selectedListVisits}
            onUnassignVisits={handleUnassignVisits}
          />
        }
        listCertificationOptions={certificationOptions}
        listDateRange={listDateRange}
        listVariant="caregiver"
        markers={markers}
        monthDate={monthDate}
        notes={notes}
        ptoRows={ptoRows}
        selectedMarkerIds={selectedAbsenceIds.map((absenceId) => `absence-${absenceId}`)}
        selectedVisitIds={selectedVisitIds}
        showCaregiverOvertimeAlerts={showCaregiverOvertimeAlerts}
        showSummaryColumn={showSummaryColumn}
        summaryVariant="caregiver"
        vacations={[]}
        view={props.view}
        visibleDates={visibleDates.dates}
        visits={props.view === "list" ? visibleVisits : visits}
        onListDateRangeChange={handleListDateRangeChange}
        onListVisibleSelectionChange={handleListVisibleSelectionChange}
        onMonthDateChange={setMonthDate}
        onPatientClick={props.actions.navigateToPatient}
        onRetry={() => void visitsQuery.refetch()}
        onToggleMarkerSelection={handleToggleMarkerSelection}
        onToggleVisitSelection={handleToggleVisitSelection}
        onVisitClick={(visit) => props.actions.navigateToVisitInstance(visit.id)}
        onVisitNameClick={(visit) => {
          if (visit.patientId != null) props.actions.navigateToPatient(visit.patientId);
          else props.actions.navigateToVisitInstance(visit.id);
        }}
      />
      <CaregiverAbsenceDeleteDialog
        absences={absencesToDelete ?? []}
        isLoading={deleteAbsencesMutation.isPending}
        isOpen={absencesToDelete !== null}
        onCancel={() => setAbsencesToDelete(null)}
        onConfirm={handleConfirmDeleteAbsences}
      />
      <CaregiverUnassignVisitsDialog
        isLoading={unassignVisitsMutation.isPending}
        isOpen={visitsToUnassign !== null}
        visits={visitsToUnassign ?? []}
        onCancel={() => setVisitsToUnassign(null)}
        onConfirm={handleConfirmUnassignVisits}
      />
    </Box>
  );
}

function filterPreservingIdentity<T>(items: T[], predicate: (item: T) => boolean): T[] {
  const next = items.filter(predicate);
  return next.length === items.length ? items : next;
}
